<script>
import bem from '@/utils/bem';
import ModalWrapper from '@/components/modals/Modal-wrapper.vue';
import UploadImages from '@/components/ux/upload/Upload-images.vue';
import { mapActions, mapState } from 'vuex';

const b = bem('custom-header-editor');

export default {
  components: {
    ModalWrapper,
    UploadImages,
  },
  data() {
    return {
      visible: true,
      logoFile: null,
      logoUrl: this.content.logoUrl || '',
      logoLink: this.content.logoLink || 'https://foreverstudios.com/',
      showTrackOrder: this.content.showTrackOrder !== undefined
        ? this.content.showTrackOrder : true,
      showMyAccount: this.content.showMyAccount !== undefined
        ? this.content.showMyAccount : true,
      showCustomLink: this.content.showCustomLink !== undefined
        ? this.content.showCustomLink : true,
      customLinkUrl: this.content.customLinkUrl
        || 'https://foreverstudios.com/forever-care/',
      customLinkText: this.content.customLinkText || 'FOREVER CARE',
    };
  },
  props: {
    content: {
      type: Object,
      required: true,
    },
    close: {
      type: Function,
      required: false,
      default: () => {},
    },
    save: {
      type: Function,
      required: false,
      default: () => {},
    },
  },
  methods: {
    ...mapActions({
      uploadEmailImageAction: 'emails/uploadEmailImage',
      clearEmailImageAction: 'emails/clearEmailImage',
    }),
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    async handleSave() {
      // If there's a new logo file, upload it
      if (this.logoFile && this.logoFile.length > 0) {
        const [file] = this.logoFile;
        if (file.file) {
          const formData = new FormData();
          formData.append('file', file.file);
          formData.append('folder', 'default');
          await this.uploadEmailImageAction(formData);
          // Wait for the image to be uploaded
          await this.$nextTick();
        } else if (file.url) {
          // Use existing URL
          this.logoUrl = file.url;
          this.saveData();
        }
      } else {
        this.saveData();
      }
    },
    saveData() {
      this.save({
        logoUrl: this.logoUrl,
        logoLink: this.logoLink,
        showTrackOrder: this.showTrackOrder,
        showMyAccount: this.showMyAccount,
        showCustomLink: this.showCustomLink,
        customLinkUrl: this.customLinkUrl,
        customLinkText: this.customLinkText,
      });
      this.clearEmailImageAction();
    },
  },
  computed: {
    ...mapState({
      emailImage: (state) => state.emails.emailImage,
    }),
  },
  watch: {
    visible(newValue) {
      if (!newValue) {
        this.close();
      }
    },
    emailImage(newValue, oldValue) {
      if ((newValue && !oldValue) || newValue?.data?.key !== oldValue?.data?.key) {
        const { data: { url } = {} } = newValue || {};
        if (url) {
          this.logoUrl = url;
          this.saveData();
        }
      }
    },
  },
  mounted() {
    // Initialize logo file if URL exists
    if (this.logoUrl) {
      this.logoFile = [{ url: this.logoUrl }];
    }
  },
};
</script>

<template>
  <modal-wrapper v-model:show="visible" width="600px">
    <div :class="b()">
      <div :class="b('title')">Custom Header Settings</div>

      <div :class="b('section')">
        <div :class="b('section-title')">Logo Settings</div>

        <div :class="b('field')">
          <div :class="b('field-label')">Logo Image</div>
          <upload-images
            v-model:files="logoFile"
            :initial-images="logoFile"
            :is-multiple="false"
          />
        </div>

        <div :class="b('field')">
          <div :class="b('field-label')">Logo Link URL</div>
          <a-input
            v-model:value="logoLink"
            placeholder="https://foreverstudios.com/"
          />
        </div>
      </div>

      <div :class="b('section')">
        <div :class="b('section-title')">Navigation Links</div>

        <div :class="b('field')">
          <div :class="b('field-toggle')">
            <span :class="b('field-label')">Show Track Order</span>
            <a-switch
              v-model:checked="showTrackOrder"
              checked-children="Yes"
              un-checked-children="No"
            />
          </div>
        </div>

        <div :class="b('field')">
          <div :class="b('field-toggle')">
            <span :class="b('field-label')">Show My Account</span>
            <a-switch
              v-model:checked="showMyAccount"
              checked-children="Yes"
              un-checked-children="No"
            />
          </div>
        </div>

        <div :class="b('field')">
          <div :class="b('field-toggle')">
            <span :class="b('field-label')">Show Custom Link</span>
            <a-switch
              v-model:checked="showCustomLink"
              checked-children="Yes"
              un-checked-children="No"
            />
          </div>
        </div>

        <template v-if="showCustomLink">
          <div :class="b('field')">
            <div :class="b('field-label')">Custom Link Text</div>
            <a-input
              v-model:value="customLinkText"
              placeholder="FOREVER CARE"
            />
          </div>

          <div :class="b('field')">
            <div :class="b('field-label')">Custom Link URL</div>
            <a-input
              v-model:value="customLinkUrl"
              placeholder="https://foreverstudios.com/forever-care/"
            />
          </div>
        </template>
      </div>

      <div :class="b('buttons')">
        <a-button
          type="primary"
          html-type="button"
          @click="handleSave"
        >
          Save
        </a-button>
        <a-button
          html-type="button"
          @click="close"
        >
          Cancel
        </a-button>
      </div>
    </div>
  </modal-wrapper>
</template>

<style scoped lang="scss">
.custom-header-editor {
  padding: 20px;

  &__title {
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 20px;
    color: #293241;
  }

  &__section {
    margin-bottom: 24px;
    padding: 16px;
    background-color: #f5f5f5;
    border-radius: 8px;
  }

  &__section-title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 16px;
    color: #5f8aa4;
  }

  &__field {
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__field-label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    color: #293241;
  }

  &__field-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__buttons {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 24px;
  }
}
</style>
